/**
 * @fileoverview Page Actions System - Context for dynamically registering action buttons in PageHeader
 *
 * This system allows components to register action buttons that appear in the page header
 * without prop drilling. Components can register/unregister actions dynamically, with control
 * over positioning via an order parameter.
 *
 * @example Basic Usage with Version Tracking
 * ```tsx
 * function MyComponent({ items }) {
 *   const { registerAction, unregisterAction } = usePageActions();
 *
 *   // Store props in ref to prevent re-registrations
 *   const propsRef = useRef({ items });
 *   propsRef.current = { items };
 *
 *   // Create stable render function with empty dependencies
 *   const renderButton = useCallback(() => {
 *     const { items } = propsRef.current;
 *     return <MyButton items={items} />;
 *   }, []);
 *
 *   // Create version number that changes when items change
 *   const version = useMemo(() => items.length, [items]);
 *
 *   useEffect(() => {
 *     registerAction('my-button', renderButton, { order: 10, version });
 *   }, [registerAction, renderButton, version]);
 *
 *   useEffect(() => {
 *     return () => unregisterAction('my-button');
 *   }, [unregisterAction]);
 * }
 * ```
 *
 * @example Conditional Registration
 * ```tsx
 * useEffect(() => {
 *   if (!featureEnabled) return;
 *   registerAction('my-action', renderButton, { order: 5, version });
 * }, [featureEnabled, registerAction, renderButton, version]);
 * ```
 *
 * **IMPORTANT - Preventing Infinite Loops:**
 * - Use the ref pattern for props to keep render function stable
 * - Use version parameter to force re-renders when data changes
 * - Never include changing props in useCallback dependencies
 *
 * **Action Ordering:**
 * - order: 0 = leftmost
 * - order: 999 = default (rightmost before static actions)
 * - Static actions (like ViewOptionsDropdown) always appear last
 */

import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Function that renders a React component
 * Stored instead of ReactNode to enable lazy rendering
 */
type RenderFunction = () => ReactNode;

/**
 * Internal representation of a registered action
 * @internal
 */
interface RegisteredAction {
  /** Unique identifier for the action */
  id: string;
  /** Render function that returns the action button */
  render: RenderFunction;
  /** Position order (lower = leftmost) */
  order: number;
  /** Version number to force re-renders when data changes */
  version: number;
}

/**
 * Context value providing action registration/unregistration functions
 */
interface PageActionsContextType {
  /**
   * Register an action button to appear in the page header
   *
   * @param id - Unique identifier for the action
   * @param render - Stable render function (use useCallback with empty deps)
   * @param options - Optional configuration
   * @param options.order - Position order (default: 999, lower = leftmost)
   * @param options.version - Version number to force re-render when data changes
   *
   * @example
   * ```tsx
   * const renderMyButton = useCallback(() => <MyButton />, []);
   * registerAction('my-button', renderMyButton, { order: 0, version: dataVersion });
   * ```
   */
  registerAction: (
    id: string,
    render: RenderFunction,
    options?: { order?: number; version?: number },
  ) => void;

  /**
   * Unregister an action button from the page header
   *
   * @param id - Unique identifier of the action to remove
   *
   * @example
   * ```tsx
   * useEffect(() => {
   *   return () => unregisterAction('my-button');
   * }, [unregisterAction]);
   * ```
   */
  unregisterAction: (id: string) => void;

  /**
   * Array of registered render functions, sorted by order
   * @internal Used by PageHeader to render actions
   */
  actions: RenderFunction[];
}

const PageActionsContext = createContext<PageActionsContextType | undefined>(undefined);

/**
 * Provider component that manages the page actions registry
 * Must wrap components that need to register actions
 *
 * @example
 * ```tsx
 * <PageActionsProvider>
 *   <PageHeader />
 *   <Content />
 * </PageActionsProvider>
 * ```
 */
export function PageActionsProvider({ children }: { children: ReactNode }) {
  const [registeredActions, setRegisteredActions] = useState<Map<string, RegisteredAction>>(
    new Map(),
  );

  const registerAction = useCallback(
    (id: string, render: RenderFunction, options?: { order?: number; version?: number }) => {
      setRegisteredActions((prev) => {
        const order = options?.order ?? 999;
        const version = options?.version ?? 0;
        const existing = prev.get(id);

        // Check if action exists with same render function, order, and version
        // Render functions should be stable (wrapped in useCallback with empty dependencies)
        // and use refs to access current props to prevent re-registrations
        // Version changes when data affecting the button changes, forcing a re-render
        if (
          existing &&
          existing.render === render &&
          existing.order === order &&
          existing.version === version
        ) {
          return prev; // No change, return same Map to prevent re-render
        }

        const next = new Map(prev);
        next.set(id, {
          id,
          render,
          order,
          version,
        });
        return next;
      });
    },
    [],
  );

  const unregisterAction = useCallback((id: string) => {
    setRegisteredActions((prev) => {
      // Only update if the action exists
      if (!prev.has(id)) {
        return prev; // No change, return same Map to prevent re-render
      }

      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Sort actions by order, then by registration time (Map insertion order)
  // Return the render functions themselves (don't call them yet)
  // PageHeader will call these functions when it actually renders
  const actions = useMemo(() => {
    return Array.from(registeredActions.values())
      .sort((a, b) => a.order - b.order)
      .map((action) => action.render);
  }, [registeredActions]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({ registerAction, unregisterAction, actions }),
    [registerAction, unregisterAction, actions],
  );

  return <PageActionsContext.Provider value={contextValue}>{children}</PageActionsContext.Provider>;
}

/**
 * Hook to access page actions registry
 *
 * Can be safely called outside of PageActionsProvider (returns no-ops).
 * This allows PageHeader to be used in contexts without a provider.
 *
 * @returns Object with registerAction, unregisterAction, and actions array
 *
 * @example
 * ```tsx
 * function MyComponent({ items }) {
 *   const { registerAction, unregisterAction } = usePageActions();
 *
 *   // Use ref pattern for stable render function
 *   const propsRef = useRef({ items });
 *   propsRef.current = { items };
 *
 *   const renderButton = useCallback(() => {
 *     const { items } = propsRef.current;
 *     return <Button items={items} />;
 *   }, []); // Empty deps = stable function reference
 *
 *   // Version changes when items change, forcing button re-render
 *   const version = useMemo(() => items.length, [items]);
 *
 *   useEffect(() => {
 *     registerAction('my-action', renderButton, { order: 5, version });
 *   }, [registerAction, renderButton, version]);
 *
 *   useEffect(() => {
 *     return () => unregisterAction('my-action');
 *   }, [unregisterAction]);
 * }
 * ```
 */
export function usePageActions() {
  const context = useContext(PageActionsContext);

  // If no provider, return safe defaults
  if (!context) {
    return {
      registerAction: () => {},
      unregisterAction: () => {},
      actions: [],
    };
  }

  return context;
}
